import { createLogger } from '@/core/helpers/logger.helper';
import type { ChessboardApi } from '@/core/network/api/chessboard/chessboard.api';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import { HttpError } from '@/core/network/http/httpError';
import type {
  ChessboardActionLogDto,
  ChessboardActionPayload,
  ChessboardDto,
  ChessMoveDto,
  CreateChessboardPayload,
  MakeMovePayload,
  MyBoardsDto,
  VettedLocationDto,
} from './chessboard.interface';

const logger = createLogger('ChessboardRepository');

export class ChessboardRepository {
  private readonly httpAPI: ChessboardApi;

  constructor(httpAPI: ChessboardApi) {
    this.httpAPI = httpAPI;
  }

  async getBoard(id: string): Promise<GamResponse<ChessboardDto> | null> {
    try {
      const response = await this.httpAPI.getBoard(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getMyBoards(): Promise<GamResponse<MyBoardsDto> | null> {
    try {
      const response = await this.httpAPI.getMyBoards();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getActiveByLocation(locationId: string): Promise<GamResponse<ChessboardDto[]> | null> {
    try {
      const response = await this.httpAPI.getActiveByLocation(locationId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getCompletedByLocation(locationId: string): Promise<GamResponse<ChessboardDto[]> | null> {
    try {
      const response = await this.httpAPI.getCompletedByLocation(locationId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getMoves(chessboardId: string): Promise<GamResponse<ChessMoveDto[]> | null> {
    try {
      const response = await this.httpAPI.getMoves(chessboardId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getActionLogs(chessboardId: string): Promise<GamResponse<ChessboardActionLogDto[]> | null> {
    try {
      const response = await this.httpAPI.getActionLogs(chessboardId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getWorldBoards(): Promise<GamResponse<ChessboardDto[]> | null> {
    try {
      const response = await this.httpAPI.getWorldBoards();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getAllBoards(): Promise<GamResponse<ChessboardDto[]> | null> {
    try {
      const response = await this.httpAPI.getAllBoards();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getVettedLocations(): Promise<GamResponse<VettedLocationDto[]> | null> {
    try {
      const response = await this.httpAPI.getVettedLocations();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async create(payload: CreateChessboardPayload): Promise<GamResponse<ChessboardDto> | null> {
    try {
      const response = await this.httpAPI.create(payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async makeMove(id: string, payload: MakeMovePayload): Promise<GamResponse<ChessboardDto> | null> {
    try {
      const response = await this.httpAPI.makeMove(id, payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async performAction(id: string, payload: ChessboardActionPayload): Promise<GamResponse<ChessboardDto> | null> {
    try {
      const response = await this.httpAPI.performAction(id, payload);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createFirestoreDoc(id: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createFirestoreDoc(id);
      return response.status === 200 ? response.data || { data: response.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async abandon(id: string): Promise<GamResponse<ChessboardDto> | null> {
    try {
      const response = await this.httpAPI.abandon(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteBoard(id: string): Promise<boolean> {
    try {
      const response = await this.httpAPI.deleteBoard(id);
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }
}
